import { Injectable } from '@angular/core';
import {
  DownsampleParams,
  SpatialWindow,
  downsampleParamsForBudget,
  resolutionOnlyParamsForBudget,
} from './telemetry-downsample';
import { AdaptiveResolutionService } from './adaptive-resolution.service';

/** The three chart families that fetch spatial telemetry independently. */
export type ChartFamily = 'temperature' | 'zone' | 'loss';

interface Viewport {
  /** Chart width in CSS pixels; 0 until the component reports one. */
  pixelWidth: number;
  /** Visible spatial window in sample indices, or null for "the whole payload window". */
  window: SpatialWindow | null;
}

/**
 * Shared publication point for each chart's viewport, so the BACKFILL can be sized
 * without holding a reference to the chart components.
 *
 * The problem this solves: the historical load is triggered by MainLayoutComponent,
 * but only the chart components know their pixel width and visible window — and
 * they live behind a `<router-outlet>`, so main-layout cannot reach them. Rather
 * than move the backfill trigger into the components (which would multiply the
 * request by however many chart screens are alive), each component PUBLISHES its
 * viewport here on init/resize/zoom and main-layout READS it when building the
 * request. One writer per family, one reader, no component coupling.
 *
 * Sizing before layout: on a cold load the backfill fires before any chart has been
 * measured, so a family with no reported width falls back to the browser viewport
 * width. The charts are effectively full-width, so that is a good approximation and
 * — critically — it means the headline backfill saving applies on first load rather
 * than only after the first resize. If even that is unavailable the width is 0,
 * which yields no params and a full-resolution request: correct, just larger.
 */
@Injectable({ providedIn: 'root' })
export class ChartViewportService {
  /**
   * Constructor-injected rather than `inject()`d so the service stays
   * constructible with `new` in specs; Angular supplies the singleton from the
   * parameter type, and the default keeps direct construction working.
   */
  constructor(
    private readonly adaptive: AdaptiveResolutionService = new AdaptiveResolutionService()
  ) {}

  private readonly viewports: Record<ChartFamily, Viewport> = {
    temperature: { pixelWidth: 0, window: null },
    zone: { pixelWidth: 0, window: null },
    loss: { pixelWidth: 0, window: null },
  };

  /** Report a chart's rendered width, in CSS pixels. */
  setPixelWidth(family: ChartFamily, pixelWidth: number): void {
    if (Number.isFinite(pixelWidth) && pixelWidth > 0) {
      this.viewports[family].pixelWidth = pixelWidth;
    }
  }

  /** Report a chart's visible spatial window (sample indices), or null to reset. */
  setWindow(family: ChartFamily, window: SpatialWindow | null): void {
    this.viewports[family].window = window;
  }

  /** Width to size this family's point budget with; falls back to the viewport. */
  pixelWidthFor(family: ChartFamily): number {
    const reported = this.viewports[family].pixelWidth;
    if (reported > 0) {
      return reported;
    }
    return this.fallbackWidth();
  }

  /** Last reported window for this family, if any. */
  windowFor(family: ChartFamily): SpatialWindow | null {
    return this.viewports[family].window;
  }

  /**
   * Downsample parameters for a family's next request.
   *
   * `defaultWindow` is the payload's natural window (LSS..LES, or 0..length for a
   * mirrored loss trace) and is used until the component reports a zoomed one.
   * Returns null — send nothing, get full resolution — whenever the flag is off or
   * the geometry is not yet usable.
   */
  paramsFor(
    family: ChartFamily,
    defaultWindow: SpatialWindow | null
  ): DownsampleParams | null {
    // One budget, resolved once against throughput, memory and the manual
    // quality override — pixel width is one of its three inputs, not the whole
    // answer. A width of 0 still propagates as full resolution.
    const budget = this.adaptive.budgetFor(family, this.pixelWidthFor(family));

    const window = this.viewports[family].window ?? defaultWindow;
    if (!window) {
      // COLD LOAD: no zoom yet and no geometry, because LSS/LES arrive in the
      // response this call is fetching. Returning null here meant the largest
      // request of the session went out at FULL RESOLUTION and only later,
      // cheaper requests were decimated. Send the budget without a window — the
      // server decimates on Resolution alone and knows the array length itself.
      // The ADAPTIVE budget applies here too: the cold load is the request most
      // worth sizing to a weak connection or a small device.
      return resolutionOnlyParamsForBudget(budget);
    }
    return downsampleParamsForBudget(window, budget);
  }

  /** The budget in force for a family, and every term behind it. */
  explainBudget(family: ChartFamily) {
    return this.adaptive.explain(family, this.pixelWidthFor(family));
  }

  /** Clear every family — project change. */
  reset(): void {
    for (const family of Object.keys(this.viewports) as ChartFamily[]) {
      this.viewports[family].window = null;
    }
  }

  private fallbackWidth(): number {
    if (typeof document === 'undefined') {
      return 0;
    }
    return document.documentElement?.clientWidth ?? 0;
  }
}
